import { RegularButton } from "@components/button"
import { Modal } from "@components/modal"
import { FlexBox } from "@components/wrapper"

import { ApplyForm } from "@interfaces/career"
import { SelectOption } from "@interfaces/misc"
import CategoryServices from "@lib/api/services/category.services"
import FormServices from "@lib/api/services/form.services"
import { FormField, FormWrapper } from "@modules/career-form/components"
import { applyFormValidation } from "@modules/career-form/constants/validations"
import { useFileForm, usePersonalOptions } from "@modules/career-form/hooks"
import clsx from "clsx"
import { Form, Formik } from "formik"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"
import { FaCheck } from "react-icons/fa"

const initialValues: ApplyForm = {
  category_id: "",
  career_position_id: "",
  user_fullname: "",
  user_email: "",
  user_phone_number: "",
  user_linkedin: "",
  user_portfolio_link: "",
  user_university: "",
  user_city: "",
  user_activities: [],
  user_description: "",
  user_expected_salary: [],
  user_skills: [],
  user_skill_tools: [],
  job_question_answer: {
    other_question_1: "",
    other_question_2: "",
    other_question_3: "",
    other_question_4: "",
  },
  user_applicant_type: "REGULAR",
}

interface Skills {
  id: number
  skill_name: string
  skill_slug: string
}

interface Tools {
  id: number
  tool_name: string
  tool_slug: string
}

function ApplyFormPage(props: React.ReactNode) {
  const { resume, errorResume, setErrorResume, handleUploadFile } = useFileForm()
  const { uniOptions, regencyOptions } = usePersonalOptions()
  const [skillOptions, setSkillOptions] = useState<SelectOption[]>([])
  const [toolOptions, setToolOptions] = useState<SelectOption[]>([])
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [modal, setModal] = useState<boolean>(false)

  const [validationRequired, setValidationRequired] = useState(false)

  const category = new CategoryServices()
  const form = new FormServices()

  const router = useRouter()
  const { career_position_id, category_id, position_name } = router.query

  useEffect(() => {
    const getSkills = async () => {
      try {
        const res = await category.getSkills()
        if (res?.isSuccess) {
          const result = res.getValue()
          const options: SelectOption[] = result.map((value: Skills) => {
            return {
              label: value.skill_name,
              value: value.id,
            }
          })
          setSkillOptions(options)
        }
      } catch (e) {
        console.log(e)
      }
    }

    const getTools = async () => {
      try {
        const res = await category.getTools()
        if (res?.isSuccess) {
          const result = res.getValue()
          const options: SelectOption[] = result.map((value: Tools) => {
            return {
              label: value.tool_name,
              value: value.id,
            }
          })
          setToolOptions(options)
        }
      } catch (e) {
        console.log(e)
      }
    }

    getSkills()
    getTools()
  }, [])

  const handleClose = async () => {
    setModal(false)
    window.location.assign("/")
  }

  function handleSubmit() {
    setValidationRequired(true)

    if (!resume) {
      setErrorResume("*Wajib sertakan file")
    }
  }

  return (
    <FormWrapper head="Form Apply - Harisenin.com">
      <Formik
        initialValues={initialValues}
        validationSchema={applyFormValidation}
        validateOnChange={validationRequired}
        validateOnBlur={validationRequired}
        onSubmit={async (values, { setFieldError }) => {
          if (!career_position_id || !category_id) {
            return setErrorMessage("Link tidak valid")
          }

          const filteredActivities = values.user_activities.filter((v) => v)
          const filteredSalaries = values.user_expected_salary.filter((v) => v)

          if (!filteredActivities.length || !filteredSalaries.length) {
            if (!filteredActivities.length) {
              setFieldError("user_activities", "*Kegiatan wajib diisi")
            }

            if (!filteredSalaries.length) {
              setFieldError("user_expected_salary", "*Ekspetasi gaji wajib diisi")
            }

            return
          }

          const valuesTemp: ApplyForm = {
            ...values,
            career_position_id: career_position_id as string,
            category_id: category_id as string,
            user_applicant_type: "REGULAR",
            user_activities: filteredActivities,
            user_expected_salary: filteredSalaries,
          }

          console.log(valuesTemp)

          if (!resume) {
            setErrorResume("*Wajib sertakan file")
            return setErrorMessage("File CV wajib di upload")
          }

          const postFormApply = await form.postFormApply(valuesTemp)

          if (postFormApply.isFailure) {
            return setErrorMessage("Terjadi Kesalahan. Silahkan Coba Lagi")
          }

          const resultPost = postFormApply.getValue()
          const bodyFormData = new FormData()
          bodyFormData.append("id", resultPost.id)
          bodyFormData.append("resume", resume)
          const uploadAsset = await form.postAssetForm(bodyFormData)

          if (uploadAsset.isFailure) {
            return setErrorMessage("Terjadi Kesalahan, Asset Tidak Terupload")
          }

          setModal(true)
        }}
      >
        {({ values, errors, isSubmitting }) => (
          <Form>
            {/* <div style={{ position: "fixed", top: 0, right: 0, zIndex: 99 }}>
              <pre>{JSON.stringify(values, null, 2)}</pre>
              <pre>{JSON.stringify(errors, null, 2)}</pre>
            </div> */}
            <FormWrapper.PersonalInfo>
              <FormField.Input
                name="user_fullname"
                label="Nama Lengkap"
                placeholder="Masukkan nama lengkap kamu"
              />

              <FormField.Input name="user_email" label="Email" placeholder="Masukkan email kamu" />

              <FormField.Phone
                name="user_phone_number"
                label="Nomor Whatsapp"
                placeholder="Masukkan nomor Whatsapp kamu"
              />

              <FormField.Input
                name="user_linkedin"
                label="Linkedin"
                placeholder="Masukkan link Linkedin kamu"
              />

              <FormField.Input
                name="user_portfolio_link"
                label="Link Portofolio"
                placeholder="Masukkan link portfolio kamu"
              />

              <FormField.FileUpload
                label="CV/Resume"
                placeholder="Unggah File CV / Resume kamu"
                onUpload={handleUploadFile}
                fileState={resume}
                errorMessage={errorResume}
              />

              <FormField.CreatableSelect
                name="user_university"
                label="Asal Universitas"
                placeholder="Masukkan Universitas kamu"
                options={uniOptions}
                noOptionsMessage="Tidak ada universitas yang ditemukan"
              />

              <FormField.Select
                name="user_city"
                label="Domisili"
                placeholder="Masukkan kota/kabupaten kamu"
                options={regencyOptions}
                noOptionsMessage="Tidak ada kota/kabupaten yang ditemukan"
              />

              <FormField.CheckRow
                name="user_activities"
                options={["Kuliah", "Mencari Kerja", "Sudah Bekerja"]}
                label="Kegiatan Saat ini"
                hasCreatable
              />

              <FormField.TextArea
                name="user_description"
                label="Deskripsi Diri"
                placeholder="Ceritakan diri anda secara seingkat"
              />
            </FormWrapper.PersonalInfo>

            <FormWrapper.OtherQuestion>
              <FormField.TextArea
                name="job_question_answer.other_question_1"
                label="Apa pengalaman dan pencapaian Anda yang berhubungan dengan posisi ini?"
                placeholder="Tulis Jawaban Kamu"
              />

              <FormField.TextArea
                name="job_question_answer.other_question_2"
                label="Jika nanti Anda terpilih, apa ekspektasi Anda terhadap posisi ini?"
                placeholder="Tulis Jawaban Kamu"
              />

              <FormField.TextArea
                name="job_question_answer.other_question_3"
                label="Jika nanti Anda terpilih, apa kontribusi yang akan Anda berikan?"
                placeholder="Tulis Jawaban Kamu"
              />

              <FormField.TextArea
                name="job_question_answer.other_question_4"
                label="Jika nanti Anda terpilih, seberapa besar komitmen Anda terhadap posisi ini?"
                placeholder="Tulis Jawaban Kamu"
              />

              <FormField.MultipleSelect
                name="user_skills"
                label="Sebutkan skills yang Anda miliki berhubungan dengan posisi ini?"
                placeholder="Masukkan Skills"
                options={skillOptions}
                noOptionsMessage="Tidak ada skill yang ditemukan"
              />

              <FormField.MultipleSelect
                name="user_skill_tools"
                label="Apa tools atau aplikasi yang sering Anda gunakan terkait dengan posisi ini?"
                placeholder="Masukkan Tools"
                options={toolOptions}
                noOptionsMessage="Tidak ada tool yang ditemukan"
              />

              <FormField.CheckRow
                name="user_expected_salary"
                options={[
                  "< 1.000.000",
                  "Rp 1.500.000 - Rp 2.000.000",
                  "Rp 2.000.000 - Rp 2.500.000",
                  "> Rp 2.500.000",
                  "Negotiable",
                ]}
                label="Ekspektasi Gaji"
                hasCreatable
                isNotRow
              />
            </FormWrapper.OtherQuestion>
            <div className="flex justify-center mb-1">
              <small className="text-red lg:text-md ">{errorMessage}</small>
            </div>
            <FlexBox justify="center">
              <RegularButton
                className={clsx("uppercase sm:w-2/5 sm:h-[48px]", "w-full h-9")}
                isFormik
                isSubmitting={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                Kirim Data
              </RegularButton>
            </FlexBox>
          </Form>
        )}
      </Formik>

      <Modal show={modal} onClose={handleClose} contentPadding="p-[30px]">
        <FlexBox direction="col" justify="center" className="gap-5" align="center">
          <FlexBox justify="center">
            <FlexBox
              className="bg-green-50 w-[100px] h-[100px] rounded-full"
              justify="center"
              align="center"
            >
              <FlexBox
                className="bg-green w-[60px] h-[60px] rounded-full"
                justify="center"
                align="center"
              >
                <FaCheck className="sm:text-2xl text-white font-semibold" />
              </FlexBox>
            </FlexBox>
          </FlexBox>

          <div className="text-center font-medium">
            Anda berhasil melamar untuk posisi {position_name}
          </div>

          <RegularButton onClick={handleClose} customPadding="py-2.5" className="w-2/5">
            Tutup
          </RegularButton>
        </FlexBox>
      </Modal>
    </FormWrapper>
  )
}

export default ApplyFormPage
